import React from "react";
import {
  Box,
  Button,
  Chip,
  Container,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import DirectionsBusRoundedIcon from "@mui/icons-material/DirectionsBusRounded";
import DirectionsCarRoundedIcon from "@mui/icons-material/DirectionsCarRounded";
import LaunchRoundedIcon from "@mui/icons-material/LaunchRounded";
import LocalPhoneRoundedIcon from "@mui/icons-material/LocalPhoneRounded";
import LocationOnRoundedIcon from "@mui/icons-material/LocationOnRounded";
import SEOHead from "../components/SEOHead";
import Reveal from "../components/Reveal";
import { clinic, openingHoursSpecification } from "../constants/clinic";

const schedule = [
  ["月", "○", "○"],
  ["火", "○", "○"],
  ["水", "○", "—"],
  ["木", "○", "○"],
  ["金", "○", "○"],
  ["土", "○", "—"],
  ["日・祝", "—", "—"],
];

const Access = () => {
  const accessStructuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "アクセス｜わかば接骨院",
    description: "瀬戸市共栄通のわかば接骨院へのアクセスと診療時間。",
    url: `${clinic.domain}/access`,
    mainEntity: {
      "@type": "MedicalClinic",
      name: clinic.name,
      url: clinic.domain,
      telephone: clinic.phone,
      address: {
        "@type": "PostalAddress",
        streetAddress: "共栄通3-5",
        addressLocality: "瀬戸市",
        addressRegion: "愛知県",
        postalCode: clinic.postalCode,
        addressCountry: "JP",
      },
      geo: {
        "@type": "GeoCoordinates",
        latitude: 35.2213662,
        longitude: 137.0798178,
      },
      openingHoursSpecification,
      hasMap: clinic.mapsUrl,
    },
  };

  return (
    <>
      <SEOHead
        title="アクセス・診療時間"
        description="瀬戸市共栄通3-5のわかば接骨院。新瀬戸駅から徒歩10分、専用駐車場9台。診療は午前9時から12時、午後4時から8時です。"
        keywords="わかば接骨院 アクセス,瀬戸市 接骨院 駐車場,新瀬戸駅 接骨院,瀬戸市共栄通 接骨院"
        canonical="/access"
        structuredData={accessStructuredData}
      />

      <Box component="section" sx={{ bgcolor: "#E8F0E8", py: { xs: 7, md: 10 }, position: "relative", overflow: "hidden" }}>
        <Box aria-hidden="true" sx={{ position: "absolute", width: 300, height: 300, borderRadius: "50%", bgcolor: "primary.light", right: -100, top: -180, opacity: .7 }} />
        <Container maxWidth="lg" sx={{ position: "relative" }}>
          <Reveal>
            <Chip label="ACCESS" sx={{ mb: 2, bgcolor: "white", color: "primary.dark", fontWeight: 700, letterSpacing: ".12em" }} />
            <Typography component="h1" variant="h1" sx={{ fontSize: { xs: "2.35rem", md: "3.5rem" }, mb: 1.5 }}>
              アクセス・診療時間
            </Typography>
            <Typography color="text.secondary" sx={{ fontSize: { sm: "1.08rem" } }}>
              瀬戸市共栄通3-5。新瀬戸駅から徒歩10分です。
            </Typography>
          </Reveal>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 7, md: 11 } }}>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1.15fr .85fr" }, gap: { xs: 4, md: 6 }, alignItems: "stretch" }}>
          <Reveal>
            <Paper elevation={0} sx={{ p: 1.2, border: "1px solid", borderColor: "divider", height: { xs: 360, md: 570 }, overflow: "hidden" }}>
              <Box
                component="iframe"
                title="わかば接骨院の地図"
                src={clinic.mapsEmbedUrl}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                sx={{ width: "100%", height: "100%", border: 0, borderRadius: 3 }}
              />
            </Paper>
          </Reveal>
          <Reveal delay={100} sx={{ height: "100%" }}>
            <Paper elevation={0} sx={{ p: { xs: 3.5, md: 5 }, border: "1px solid", borderColor: "divider", height: "100%" }}>
              <Typography variant="h4" sx={{ mb: 3 }}>わかば接骨院</Typography>
              <Stack spacing={2.7}>
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                  <LocationOnRoundedIcon sx={{ color: "primary.dark", mt: .4 }} />
                  <Box>
                    <Typography sx={{ fontWeight: 700, mb: .4 }}>所在地</Typography>
                    <Typography color="text.secondary">
                      〒{clinic.postalCode}<br />{clinic.address}
                    </Typography>
                  </Box>
                </Stack>
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                  <AccessTimeRoundedIcon sx={{ color: "primary.dark", mt: .4 }} />
                  <Box>
                    <Typography sx={{ fontWeight: 700, mb: .4 }}>診療時間</Typography>
                    <Typography color="text.secondary">
                      午前9時から12時<br />
                      午後4時から8時<br />
                      <Box component="span" sx={{ fontSize: ".9rem" }}>水曜・土曜は午前のみ</Box>
                    </Typography>
                  </Box>
                </Stack>
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                  <LocalPhoneRoundedIcon sx={{ color: "primary.dark", mt: .4 }} />
                  <Box>
                    <Typography sx={{ fontWeight: 700, mb: .4 }}>電話番号</Typography>
                    <Typography component="a" href={clinic.phoneHref} sx={{ color: "primary.dark", fontWeight: 700, fontSize: "1.25rem", textDecoration: "none" }}>
                      {clinic.phone}
                    </Typography>
                  </Box>
                </Stack>
              </Stack>
              <Stack direction={{ xs: "column", sm: "row", md: "column" }} spacing={1.25} sx={{ mt: 4 }}>
                <Button component="a" href={clinic.mapsUrl} target="_blank" rel="noreferrer" variant="contained" startIcon={<LaunchRoundedIcon />}>
                  Googleマップで開く
                </Button>
                <Button component="a" href={clinic.phoneHref} variant="outlined" startIcon={<LocalPhoneRoundedIcon />}>
                  電話で問い合わせる
                </Button>
              </Stack>
            </Paper>
          </Reveal>
        </Box>
      </Container>

      <Box component="section" sx={{ bgcolor: "#F5F5F4", py: { xs: 8, md: 11 } }}>
        <Container maxWidth="lg">
          <Reveal sx={{ textAlign: "center", mb: 5 }}>
            <Typography component="h2" variant="h2" sx={{ fontSize: { xs: "2rem", md: "2.7rem" }, mb: 1.5 }}>
              お越しになる方へ
            </Typography>
            <Typography color="text.secondary">電車・バス・お車でご来院いただけます。</Typography>
          </Reveal>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2.5 }}>
            <Reveal>
              <Paper elevation={0} sx={{ p: { xs: 3.5, md: 4.5 }, border: "1px solid", borderColor: "divider", height: "100%" }}>
                <DirectionsBusRoundedIcon sx={{ color: "primary.main", fontSize: 38, mb: 2 }} />
                <Typography variant="h5" sx={{ mb: 1.5 }}>公共交通機関でお越しの方</Typography>
                <Typography color="text.secondary">
                  名鉄瀬戸線「新瀬戸駅」から徒歩10分<br />
                  瀬戸市バス「共栄通」停留所から徒歩2分<br />
                  名鉄バス「瀬戸市役所前」停留所から徒歩3分
                </Typography>
              </Paper>
            </Reveal>
            <Reveal delay={80}>
              <Paper elevation={0} sx={{ p: { xs: 3.5, md: 4.5 }, border: "1px solid", borderColor: "divider", height: "100%" }}>
                <DirectionsCarRoundedIcon sx={{ color: "primary.main", fontSize: 38, mb: 2 }} />
                <Typography variant="h5" sx={{ mb: 1.5 }}>お車でお越しの方</Typography>
                <Typography color="text.secondary">
                  専用駐車場を9台分ご用意しています。<br />
                  駐車場所が分からない場合は、受付でお声がけください。
                </Typography>
              </Paper>
            </Reveal>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 11 } }}>
        <Reveal>
          <Box sx={{ textAlign: "center", mb: 5 }}>
            <Typography component="h2" variant="h2" sx={{ fontSize: { xs: "2rem", md: "2.7rem" }, mb: 1.5 }}>診療時間</Typography>
            <Typography color="text.secondary">午後の診療は、午後4時から8時までです。</Typography>
          </Box>
          <Paper elevation={0} sx={{ border: "1px solid", borderColor: "divider", overflowX: "auto", maxWidth: 900, mx: "auto" }}>
            <Box sx={{ display: "grid", gridTemplateColumns: "1.5fr repeat(7, minmax(52px, 1fr))", overflowX: "auto", minWidth: 680 }}>
              <Box sx={{ p: 2, bgcolor: "primary.dark", color: "white", fontWeight: 700 }}>時間</Box>
              {schedule.map(([day]) => <Box key={day} sx={{ p: 2, bgcolor: "primary.dark", color: "white", textAlign: "center", fontWeight: 700 }}>{day}</Box>)}
              <Box sx={{ p: 2, bgcolor: "#F5F5F4", fontWeight: 700 }}>午前9時から12時</Box>
              {schedule.map(([day, morning]) => <Box key={`${day}-am`} sx={{ p: 2, bgcolor: "#F5F5F4", textAlign: "center", color: morning === "○" ? "primary.dark" : "text.secondary", fontWeight: 700 }}>{morning}</Box>)}
              <Box sx={{ p: 2, fontWeight: 700 }}>午後4時から8時</Box>
              {schedule.map(([day, , afternoon]) => <Box key={`${day}-pm`} sx={{ p: 2, textAlign: "center", color: afternoon === "○" ? "primary.dark" : "text.secondary", fontWeight: 700 }}>{afternoon}</Box>)}
            </Box>
          </Paper>
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", mt: 2.5 }}>
            ○ 診療　— 休診　　日曜・祝日は休診です。
          </Typography>
        </Reveal>
      </Container>
    </>
  );
};

export default Access;
